// toast.component.ts
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { Toast } from './toast.service';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div 
      class="toast toast-{{toast.type}}" 
      [class.toast-closable]="toast.closable"
      [@slideIn]="'in'">
      <div class="toast-content">
        <span class="toast-icon">{{getIcon()}}</span>
        <span class="toast-message">{{toast.message}}</span>
        <button 
          *ngIf="toast.closable" 
          class="toast-close" 
          (click)="onClose()">×</button>
      </div>
    </div>
  `,
  styles: [`
    .toast {
      display: flex;
      align-items: center;
      min-width: 300px;
      max-width: 500px;
      padding: 12px 16px;
      margin-bottom: 8px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.4;
      transition: all 0.3s ease;
    }

    .toast-content {
      display: flex;
      align-items: center;
      width: 100%;
    }

    .toast-icon {
      margin-right: 12px;
      font-size: 18px;
      flex-shrink: 0;
    }

    .toast-message {
      flex: 1;
      word-break: break-word;
    }

    .toast-close {
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      margin-left: 12px;
      padding: 0;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.7;
      transition: opacity 0.2s ease;
    }

    .toast-close:hover {
      opacity: 1;
    }

    .toast-success {
      background-color: #d4edda;
      border-left: 4px solid #28a745;
      color: #155724;
    }

    .toast-error {
      background-color: #f8d7da;
      border-left: 4px solid #dc3545;
      color: #721c24;
    }

    .toast-warning {
      background-color: #fff3cd;
      border-left: 4px solid #ffc107;
      color: #856404;
    }

    .toast-info {
      background-color: #d1ecf1;
      border-left: 4px solid #17a2b8;
      color: #0c5460;
    }

    .toast:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }
  `],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(24px)' }),
        animate('180ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        animate('140ms ease-in', style({ opacity: 0, transform: 'translateX(24px)' })),
      ]),
    ])
  ]
})
export class ToastComponent {
  @Input() toast!: Toast;
  @Output() close = new EventEmitter<string>();

  getIcon(): string {
    switch (this.toast.type) {
      case 'success': return '✓';
      case 'error': return '✕';
      case 'warning': return '⚠';
      case 'info': return 'ℹ';
      default: return 'ℹ';
    }
  }

  onClose(): void {
    this.close.emit(this.toast.id);
  }
}
